/**
 * Toolbar - Top Application Bar Component
 * 
 * A consistent top toolbar that contains navigation context
 * and global actions.
 * 
 * Features:
 * - Breadcrumbs or navigation context as first element
 * - Right-side actions (theme, notifications, user menu)
 * - Consistent height and styling
 * 
 * Note: This is a layout component. Global action content should
 * be composed from DS primitives.
 */

import * as React from "react"
import { cn } from "../../shared/lib/cn"
import { Button } from "./button"
import { Tooltip } from "./tooltip"

/* ============================================
   Types
   ============================================ */

interface ToolbarProps extends React.HTMLAttributes<HTMLElement> {
  /** Content for the left section (e.g., breadcrumbs or navigation context) */
  leftContent?: React.ReactNode
  /** Content for the center section (optional) */
  centerContent?: React.ReactNode
  /** Content for the right section (e.g., theme toggle, notifications, user menu) */
  rightContent?: React.ReactNode
  /** Whether the toolbar is sticky */
  sticky?: boolean
  /** Visual surface treatment */
  surface?: ToolbarSurface
}

type ToolbarSurface = "solid" | "home" | "home-scrolled" | "translucent"

interface ToolbarSectionProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Alignment: 'left', 'center', or 'right' */
  align?: 'left' | 'center' | 'right'
}

interface ToolbarDividerProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Orientation: 'vertical' (default) or 'horizontal' */
  orientation?: 'vertical' | 'horizontal'
}

interface ToolbarItemProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Icon to display */
  icon?: React.ReactNode
  /** Whether this is the active item */
  isActive?: boolean
  /** Show as a badge with count */
  badge?: number | string
  /** Tooltip text */
  tooltip?: string
}

/* ============================================
   Toolbar Container
   ============================================ */

const Toolbar = React.forwardRef<HTMLElement, ToolbarProps>(
  ({
    className,
    leftContent,
    centerContent,
    rightContent,
    sticky = true,
    surface = "solid",
    children,
    ...props
  }, ref) => {
    return (
      <header
        ref={ref}
        data-surface={surface}
        className={cn(
          "relative h-12 flex items-center justify-between px-4 transition-colors duration-200",
          surface === "solid" && "bg-white dark:bg-dark-surface border-b border-gray-200 dark:border-dark-border/30",
          surface === "home" && "bg-transparent border-b-0",
          (surface === "translucent" || surface === "home-scrolled") && [
            "border-b-0 backdrop-blur-sm",
            "[--toolbar-fade-solid:rgb(255_255_255_/_1)] [--toolbar-fade-mid:rgb(255_255_255_/_0.72)] [--toolbar-fade-clear:rgb(255_255_255_/_0)]",
            "dark:[--toolbar-fade-solid:rgb(var(--color-dark-surface)_/_1)] dark:[--toolbar-fade-mid:rgb(var(--color-dark-surface)_/_0.66)] dark:[--toolbar-fade-clear:rgb(var(--color-dark-surface)_/_0)]",
            "bg-[linear-gradient(to_bottom,var(--toolbar-fade-solid)_0%,var(--toolbar-fade-mid)_32%,var(--toolbar-fade-clear)_72%,var(--toolbar-fade-clear)_100%)]",
          ],
          sticky && "sticky top-0 z-40",
          className
        )}
        {...props}
      >
        {(surface === "home" || surface === "home-scrolled") && (
          <div
            aria-hidden="true"
            data-testid="toolbar-accent-bar"
            className="pointer-events-none absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-eliza-red via-eliza-red-light to-eliza-red-coral"
          />
        )}

        {/* Left section - Breadcrumbs or navigation context */}
        <div className="flex items-center gap-4">
          {leftContent}
        </div>

        {/* Center content (optional) */}
        {(centerContent || children) && (
          <div className="flex-1 flex items-center justify-center">
            {centerContent || children}
          </div>
        )}

        {/* Spacer if no center content */}
        {!centerContent && !children && <div className="flex-1" />}

        {/* Right section */}
        <div className="flex items-center gap-2">
          {rightContent}
        </div>
      </header>
    )
  }
)
Toolbar.displayName = "Toolbar"

/* ============================================
   Toolbar Section (for grouping items)
   ============================================ */

const ToolbarSection = React.forwardRef<HTMLDivElement, ToolbarSectionProps>(
  ({ className, align = 'left', children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          "flex items-center gap-2",
          align === 'center' && "justify-center",
          align === 'right' && "justify-end",
          className
        )}
        {...props}
      >
        {children}
      </div>
    )
  }
)
ToolbarSection.displayName = "ToolbarSection"

/* ============================================
   Toolbar Divider
   ============================================ */

const ToolbarDivider = React.forwardRef<HTMLDivElement, ToolbarDividerProps>(
  ({ className, orientation = 'vertical', ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          "bg-border",
          orientation === 'vertical' ? "w-px h-6 mx-2" : "h-px w-full my-2",
          className
        )}
        {...props}
      />
    )
  }
)
ToolbarDivider.displayName = "ToolbarDivider"

/* ============================================
   Toolbar Item (Icon button using DS Button)
   ============================================ */

const ToolbarItem = React.forwardRef<HTMLButtonElement, ToolbarItemProps>(
  ({ className, icon, isActive, badge, tooltip, children, ...props }, ref) => {
    const buttonElement = (
      <div className="relative">
        <Button
          ref={ref}
          variant="ghost"
          size="sm"
          className={cn(
            "relative p-2 h-auto",
            isActive && "text-eliza-red bg-eliza-red/10",
            className
          )}
          {...props}
        >
          {icon}
          {children}
        </Button>
        
        {/* Badge */}
        {badge !== undefined && badge !== 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-eliza-red text-white text-[10px] font-semibold rounded-full flex items-center justify-center pointer-events-none">
            {typeof badge === 'number' && badge > 99 ? '99+' : badge}
          </span>
        )}
      </div>
    )

    if (tooltip) {
      return (
        <Tooltip content={tooltip} position="bottom">
          {buttonElement}
        </Tooltip>
      )
    }

    return buttonElement
  }
)
ToolbarItem.displayName = "ToolbarItem"

/* ============================================
   Toolbar Text Button (using DS Button)
   ============================================ */

interface ToolbarTextButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  icon?: React.ReactNode
}

const ToolbarTextButton = React.forwardRef<HTMLButtonElement, ToolbarTextButtonProps>(
  ({ className, icon, children, ...props }, ref) => {
    return (
      <Button
        ref={ref}
        variant="ghost"
        size="sm"
        className={cn(
          "gap-2 text-xs font-medium",
          className
        )}
        {...props}
      >
        {icon}
        {children}
      </Button>
    )
  }
)
ToolbarTextButton.displayName = "ToolbarTextButton"

/* ============================================
   Exports
   ============================================ */

export {
  Toolbar,
  ToolbarSection,
  ToolbarDivider,
  ToolbarItem,
  ToolbarTextButton,
}

export type {
  ToolbarProps,
  ToolbarSurface,
  ToolbarSectionProps,
  ToolbarItemProps,
}
